import { Box, Stack, Typography } from "@mui/material"
import { alpha } from "@mui/material/styles"
import { find, isArray } from "lodash"

import type { FC, FCWC, ResponsiveImageType } from ".."
import { Button, Container, Image, Link, SvgIcon } from ".."
import type { LinkType } from "../../graphql/types"

type ExploreItem = {
  title: string
  self_link: LinkType
  image: ResponsiveImageType
}

type ExploreProps = {
  title: string
  subtitle?: string
  description?: string
  items: ExploreItem[]
  link?: LinkType
}

const ExploreList: FC<ExploreProps> = ({ title, subtitle, description, items, link }) => {
  if (!title || !isArray(items)) {
    return null
  }

  return (
    <ExploreListSection>
      <ExploreListContent>
        {subtitle && <ExploreListSubtitle subtitle={subtitle} />}
        <ExploreListTitle title={title} />
        {description && <ExploreListDescription description={description} />}
      </ExploreListContent>

      <Stack
        mb={{ xs: 15, md: 30 }}
        position="relative"
        sx={{
          "isolation": "isolate",
          "& > div:first-of-type": {
            "borderTop": (theme) => `1px solid ${alpha(theme.palette["secondary.800"].main, 0.1)}`,
            "&:hover": {
              borderTopColor: "transparent",
            },
          },
        }}
      >
        {items?.map((item, i) => (
          <ExploreListItem key={i} index={i} {...item} />
        ))}
      </Stack>
      {link && (
        <Box textAlign="center">
          <Button
            href={link.uri}
            variant="neutral.secondary"
            sx={{
              display: {
                xs: "flex",
                md: "inline-flex",
              },
              width: {
                xs: "100%",
                md: "auto",
              },
            }}
          >
            {link.title}
          </Button>
        </Box>
      )}
    </ExploreListSection>
  )
}
export default ExploreList

/** A sub-component for the section container of the Places To Go List sss */
const ExploreListSection: FCWC = ({ children }) => {
  return (
    <Box
      color={(theme) => theme.palette["neutral.700"].main}
      bgcolor={(theme) => theme.palette["secondary.200"].main}
    >
      <Container maxWidth={1300} pt={{ xs: 20, md: 55 }} pb={{ xs: 20, md: 40 }}>
        {children}
      </Container>
    </Box>
  )
}

/** A sub-component for the text content of the Places To Go List */
const ExploreListContent: FCWC = ({ children }) => (
  <Stack
    rowGap={{ xs: 6, md: 10 }}
    maxWidth={750}
    mx="auto"
    mb={{ xs: 15, md: 45 }}
    textAlign={{ md: "center" }}
  >
    {children}
  </Stack>
)

/** A sub-component for the title of the Places To Go List */
const ExploreListTitle: FC<{ title: string }> = ({ title }) => (
  <Typography
    component="h2"
    variant="heading.2xl"
    sx={(theme) => ({ color: theme.palette["secondary.800"].main })}
  >
    {title}
  </Typography>
)

/** A sub-component for the subtitle of the Places To Go List */
const ExploreListSubtitle: FC<{ subtitle: string }> = ({ subtitle }) => (
  <Typography
    component="h3"
    variant="body.lg"
    sx={(theme) => ({ color: theme.palette["secondary.800"].main })}
  >
    {subtitle}
  </Typography>
)

/** A sub-component for the description of the Places To Go List */
const ExploreListDescription: FC<{ description: string }> = ({ description }) => (
  <Typography
    component="div"
    variant="body.md"
    sx={(theme) => ({ color: theme.palette["secondary.800"].main })}
  >
    <Box dangerouslySetInnerHTML={{ __html: description }} />
  </Typography>
)

/** A sub-component for the list item of the Places To Go List */
type ItemProps = ExploreItem & {
  index: number
}
const ExploreListItem: FC<ItemProps> = ({ index, title, image, self_link }) => {
  const teaserImage = find(image?.variants, { style: "teaser_20_23_3xl" })

  return (
    <Stack
      width="100%"
      direction="row"
      alignItems="center"
      columnGap={{ xs: 5, md: 10 }}
      px={{ xs: 3, md: 5 }}
      py={{ xs: 5, md: 7 }}
      borderTop={(theme) => `1px solid ${alpha(theme.palette["secondary.800"].main, 0.1)}`}
      mb="-1px"
      position="relative"
      sx={{
        "transition": (theme) => theme.transitions.create("all"),

        "&:last-of-type": {
          borderBottom: (theme) => `1px solid ${alpha(theme.palette["secondary.800"].main, 0.1)}`,
        },

        "& > .explore-list-img": {
          opacity: 0,
          transition: (theme) =>
            theme.transitions.create("all", {
              duration: theme.transitions.duration.standard * 2,
            }),
        },

        "& > .explore-icon svg": {
          transition: (theme) => theme.transitions.create("transform"),
        },

        "&:hover": {
          borderTopColor: "transparent",
          borderBottomColor: "transparent",
          borderRadius: 1.5,
          bgcolor: (theme) => theme.palette["neutral.100"].main,
          zIndex: 1,
        },

        "&:hover > .explore-list-img": {
          opacity: 1,
          transform: `translateY(-50%) rotate(${Math.pow(-1, index) * (4 - 1.5 * index)}deg)`,
        },

        "&:hover + *": {
          borderTopColor: "transparent",
        },

        "&:hover > .explore-icon svg": {
          transform: "translateX(5px) rotate(180deg)",
        },
      }}
    >
      {self_link && <Link type="overlay" href={self_link.uri} title={title} />}
      <Typography
        variant="body.sm"
        sx={(theme) => ({ color: theme.palette["secondary.800"].main })}
      >
        {index < 9 ? `0${index + 1}` : index + 1}
      </Typography>
      <Typography
        variant="heading.sm"
        component="h2"
        sx={(theme) => ({ color: theme.palette["secondary.800"].main, fontSize: 26 })}
      >
        {title}
      </Typography>
      <Stack className="explore-icon" direction="row" alignItems="center" columnGap={3} ml="auto">
        <SvgIcon
          variant="arrow"
          sx={(theme) => ({
            color: theme.palette["secondary.800"].main,
            transform: "rotate(180deg)",
            fill: (theme) => theme.palette["secondary.800"].main,
          })}
          size={16}
        />
      </Stack>
      {teaserImage && (
        <Box
          className="explore-list-img"
          sx={{
            width: 270,
            height: 330,
            aspectRatio: 27 / 33,
            borderRadius: 1.5,
            display: { xs: "none", lg: "block" },
            position: "absolute",
            overflow: "hidden",
            top: "50%",
            right: 200,
            zIndex: 2,
          }}
        >
          <Image
            src={teaserImage?.url}
            alt={teaserImage?.alt}
            borderRadius={1.5}
            sx={{
              display: "block",
              width: "100%",
              height: "100%",
              pointerEvents: "none",
              objectFit: "cover",
            }}
          />
        </Box>
      )}
    </Stack>
  )
}
